import { Moment } from 'moment';
import * as React from 'react';
import { Button, Label } from 'reactstrap';
import TimePicker from './TimePicker';
import Spinner from 'components/shared/Spinner';
import AppState from 'AppState';
import { toKebabCase } from 'util/StringUtil';

/** Lazily loaded types. These should ONLY to be used as type parameters for React.lazy. */
import { SingleDatePicker as singleDatePickerType__, SingleDatePickerShape } from 'react-dates';

/** Lazy loading */
const SingleDatePicker = React.lazy<singleDatePickerType__>(
	() => import(/* webpackChunkName: "single-date-picker-component" */ 'react-dates/lib/components/SingleDatePicker'),
);

interface DateTimePickerProps {
	/** The initial date and time value to use on the component. */
	dateTime: Moment | null;

	/** Whether this component is disabled. */
	disabled: boolean;

	/** Callback when a new date or time has been selected in the date/time picker. */
	onDateTimeChange: (value: Moment | null) => void;

	/** Unique name of the date time picker */
	name: string;

	/** Show all the minutes in the minute dropdown */
	showAllMinutes?: boolean;

	/** Props for the underlying SingleDatePicker */
	singleDatePickerShape?: SingleDatePickerShape;

	/** Index for building testid */
	index?: number;
}

interface DateTimePickerState {
	/** Whether the inner date component has focus. */
	focused: boolean;

	/** The current date/time value of the component. */
	date: Moment | null;
}

/**
 * Component used to handle picking dates and times.
 *
 * @class DateTimePicker
 * @extends {(React.Component<DateTimePickerProps, DateTimePickerState>)}
 */
class DateTimePicker extends React.Component<DateTimePickerProps, DateTimePickerState> {
	static defaultProps = {
		index: 0,
	};

	constructor(props) {
		super(props);
		const { dateTime } = this.props;
		const localizedDate = dateTime && dateTime.tz(AppState.settings.timeZone);

		this.state = {
			focused: false,
			date: localizedDate,
		};
	}

	/**
	 * Event that takes place when the user changes the date, but not the time.
	 *
	 * @private
	 * @memberof DateTimePicker
	 */
	private onDateChange = (date: Moment | null) => {
		// SingleDatePicker doesn't care about timezones so we must forcibly convert it to 12PM.
		const newDate = date && date.tz(AppState.settings.timeZone).hour(12);
		this.onDateTimeChange(newDate);
	};

	/**
	 * Event that takes place when the user changes the date or the time.
	 *
	 * @private
	 * @memberof DateTimePicker
	 */
	private onDateTimeChange = (date: Moment | null) => {
		this.setState({ date });
		this.props.onDateTimeChange(date);
	};

	/** Clears the date and time, blanking the date and time pieces of the component. */
	private clearDateTime = () => {
		this.setState({ date: null });
		this.props.onDateTimeChange(null);
	};

	/** Event that takes place when the user changes the focus on the inner date component. */
	private onFocusChange = ({ focused }) => {
		this.setState({ focused: !!focused });
	};

	public render() {
		const { name } = this.props;

		return (
			<div className="datetime-picker">
				<React.Suspense
					fallback={
						<div className="datetime-picker__loading">
							<Spinner />
						</div>
					}
				>
					<div
						className="datetime-picker__item"
						data-testid={`datetimepicker-${toKebabCase(this.props.name)}-${this.props.index}`}
					>
						<Label for={name} className="visually-hidden">
							Select date
						</Label>
						<SingleDatePicker
							id={name}
							showDefaultInputIcon
							small
							disabled={this.props.disabled}
							date={this.state.date}
							focused={this.state.focused}
							onDateChange={this.onDateChange}
							onFocusChange={this.onFocusChange}
							{...this.props.singleDatePickerShape}
						/>
					</div>
					<div className="datetime-picker__item">
						<TimePicker
							value={this.state.date}
							disabled={this.props.disabled || !this.state.date}
							onTimeChange={this.onDateTimeChange}
							timeIncrement={this.props.showAllMinutes ? 1 : undefined}
						/>
					</div>
					<div className="datetime-picker__item">
						<Button
							type="button"
							color="primary-alt"
							className="btn-block datetime-picker__submit"
							outline
							onClick={this.clearDateTime}
							disabled={this.props.disabled}
							data-testid="clear-scheduled-date"
						>
							Clear
						</Button>
					</div>
					<div className="datetime-picker__item">
						<span className="datetime-picker__timezone-description">
							Time zone:{' '}
							<span className="datetime-picker__timezone-name">{AppState.settings.timeZone}</span>.
						</span>
					</div>
				</React.Suspense>
			</div>
		);
	}
}

export default DateTimePicker;
